import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Customer, ContactPerson } from '../../types/customer';
import toast from 'react-hot-toast';
import {
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface SendCustomerLoginInviteProps {
  customer: Customer;
  contactPerson?: ContactPerson; // Optional: specific contact person to invite
  onSuccess?: () => void;
}

const SendCustomerLoginInvite: React.FC<SendCustomerLoginInviteProps> = ({ customer, contactPerson, onSuccess }) => {
  const [isSending, setIsSending] = useState(false);

  const generateToken = () => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  };

  const handleSendInvite = async () => {
    // Check if specific contact person already has account
    if (contactPerson?.hasUserAccount) {
      toast.error('Ez a kapcsolattartó már rendelkezik felhasználói fiókkal');
      return;
    }

    // Check if customer (old system) already has account
    if (!contactPerson && customer.hasUserAccount) {
      toast.error('Ez az ügyfél már rendelkezik felhasználói fiókkal');
      return;
    }

    setIsSending(true);

    try {
      // Generate unique token
      const token = generateToken();
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7); // 7 days expiry

      // If specific contact person, update their invitation status
      if (contactPerson && customer.contactPersons) {
        const updatedContactPersons = customer.contactPersons.map(cp =>
          cp.id === contactPerson.id
            ? { ...cp, invitationSentAt: new Date(), registrationToken: token, registrationTokenExpiry: expiryDate }
            : cp
        );

        await updateDoc(doc(db, 'customers', customer.id), {
          contactPersons: updatedContactPersons,
          updatedAt: serverTimestamp()
        });
      } else {
        // Old system: update customer directly
        await updateDoc(doc(db, 'customers', customer.id), {
          registrationToken: token,
          registrationTokenExpiry: expiryDate,
          updatedAt: serverTimestamp()
        });
      }

      // Generate signup link
      const signupLink = `${window.location.origin}/customer-signup/${token}`;

      // Send email (TODO: implement email service)
      // For now, just copy to clipboard
      await navigator.clipboard.writeText(signupLink);

      const recipientName = contactPerson ? contactPerson.name : (customer.type === 'organization' ? customer.companyName : `${customer.firstName} ${customer.lastName}`);
      toast.success(`Regisztrációs link vágólapra másolva (${recipientName})! (Email küldés hamarosan elérhető)`);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error('Hiba történt a meghívó küldése során');
    } finally {
      setIsSending(false);
    }
  };

  // Check if specific contact person has account (must have both hasUserAccount and userId)
  if (contactPerson?.hasUserAccount && contactPerson?.userId) {
    return (
      <button
        disabled
        className="inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium text-green-700 bg-green-100 rounded-md dark:bg-green-900/30 dark:text-green-300 cursor-not-allowed"
        title="Már rendelkezik felhasználói fiókkal"
      >
        <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
        <span className="hidden sm:inline ml-1">Van fiókja</span>
      </button>
    );
  }

  // Check if customer (old system) already has account
  if (!contactPerson && customer.hasUserAccount) {
    return (
      <button
        disabled
        className="inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium text-green-700 bg-green-100 rounded-md dark:bg-green-900/30 dark:text-green-300 cursor-not-allowed"
        title="Már rendelkezik felhasználói fiókkal"
      >
        <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
        <span className="hidden sm:inline ml-1">Van fiókja</span>
      </button>
    );
  }

  // Check if invite is pending for specific contact person
  if (contactPerson?.invitationSentAt && contactPerson?.registrationTokenExpiry) {
    const expiryDate = contactPerson.registrationTokenExpiry instanceof Date 
      ? contactPerson.registrationTokenExpiry 
      : contactPerson.registrationTokenExpiry.toDate();
    const isExpired = expiryDate < new Date();
    
    if (!isExpired) {
      return (
        <button
          onClick={handleSendInvite}
          disabled={isSending}
          className="inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium text-yellow-700 bg-yellow-100 rounded-md hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:hover:bg-yellow-900/50 transition-colors"
          title="Meghívó elküldve, kattints az újraküldéshez"
        >
          <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
          <span className="hidden sm:inline ml-1">Függőben</span>
        </button>
      );
    }
  }

  // Check if invite is pending for customer (old system)
  if (!contactPerson && customer.registrationToken && customer.registrationTokenExpiry) {
    const isExpired = new Date(customer.registrationTokenExpiry) < new Date();
    
    if (!isExpired) {
      return (
        <button
          onClick={handleSendInvite}
          disabled={isSending}
          className="inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium text-yellow-700 bg-yellow-100 rounded-md hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:hover:bg-yellow-900/50 transition-colors"
          title="Meghívó elküldve, kattints az újraküldéshez"
        >
          <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
          <span className="hidden sm:inline ml-1">Függőben</span>
        </button>
      );
    }
  }

  return (
    <button
      onClick={handleSendInvite}
      disabled={isSending}
      className="inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium text-primary-700 bg-primary-100 rounded-md hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      title="Login meghívó küldése"
    >
      {isSending ? (
        <>
          <div className="animate-spin h-2.5 w-2.5 sm:h-3 sm:w-3 sm:mr-1 border border-primary-600 border-t-transparent rounded-full"></div>
          <span className="hidden sm:inline ml-1">Küldés...</span>
        </>
      ) : (
        <>
          <PaperAirplaneIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
          <span className="hidden sm:inline ml-1">Meghívó</span>
        </>
      )}
    </button>
  );
};

export default SendCustomerLoginInvite;
